import { useEffect, useState } from "react";
import { Loader2, Save } from "lucide-react";
import { toast } from "sonner";
import { useQueryClient } from "@tanstack/react-query";
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from "@/components/ui/select";
import { http, errMsg } from "@/lib/api";
import { Field, Card } from "@/components/checkout/Field";
import { ImageInput } from "./ImageInput";
import { PriceOptionsEditor } from "./PriceOptionsEditor";

const WAIT = ["10 min", "20 min", "30 min", "40 min", "1 hr"];

export const AdminSettings = () => {
  const [s, setS] = useState(null);
  const [saving, setSaving] = useState(false);
  const qc = useQueryClient();

  useEffect(() => {
    http.get("/admin/settings").then((r) => setS(r.data)).catch((e) => toast.error(errMsg(e)));
  }, []);

  if (!s) return <Loader2 className="w-6 h-6 animate-spin text-amber-400 mx-auto mt-10" />;
  const set = (k) => (e) => setS({ ...s, [k]: e?.target ? e.target.value : e });

  const save = async () => {
    if (!s.business_name.trim()) return toast.error("El nombre del negocio es obligatorio");
    setSaving(true);
    try {
      const { data } = await http.put("/admin/settings", { ...s, delivery_fee: Number(s.delivery_fee) || 0 });
      setS(data);
      qc.invalidateQueries({ queryKey: ["menu"] });
      toast.success("Configuración guardada");
    } catch (e) {
      toast.error(errMsg(e));
    } finally {
      setSaving(false);
    }
  };

  return (
    <div className="space-y-4 pb-24" data-testid="admin-settings">
      <Card title="Datos del local">
        <Field label="Nombre del negocio" required value={s.business_name} onChange={set("business_name")} testid="settings-name-input" />
        <Field label="Dirección del local" value={s.address} onChange={set("address")} testid="settings-address-input" />
        <ImageInput label="Logo" value={s.logo} onChange={set("logo")} testid="settings-logo" max={400} />
        <ImageInput label="Imagen de fondo / Banner" value={s.banner} onChange={set("banner")} testid="settings-banner" max={1600} />
      </Card>
      <Card title="Pedidos y entrega">
        <Field label="WhatsApp receptor (con código de país, ej: 5491123456789)" value={s.whatsapp} onChange={set("whatsapp")} testid="settings-whatsapp-input" inputMode="tel" />
        <div>
          <span className="text-xs text-[#A89F91] font-medium">Tiempo de espera / tardanza</span>
          <Select value={s.wait_time} onValueChange={set("wait_time")}>
            <SelectTrigger className="mt-1.5 field-dark rounded-xl h-12" data-testid="settings-wait-select"><SelectValue /></SelectTrigger>
            <SelectContent className="bg-[#1C1515] border-[#2E2323] text-amber-50">
              {WAIT.map((w) => <SelectItem key={w} value={w} data-testid={`settings-wait-option-${w.replace(" ", "")}`}>{w}</SelectItem>)}
            </SelectContent>
          </Select>
        </div>
        <Field label="Costo de envío (0 = sin cargo / a coordinar)" type="number" value={s.delivery_fee} onChange={set("delivery_fee")} testid="settings-delivery-fee-input" />
      </Card>
      <Card title="Datos para transferencias">
        <Field label="Alias" value={s.alias} onChange={set("alias")} testid="settings-alias-input" />
        <Field label="CBU / CVU" value={s.cbu} onChange={set("cbu")} testid="settings-cbu-input" />
        <Field label="Titular de la cuenta" value={s.holder} onChange={set("holder")} testid="settings-holder-input" />
      </Card>
      <Card title="Tipos de masa (categorías con masa activada)">
        <PriceOptionsEditor title="Masas (precio adicional)" items={s.doughs} onChange={set("doughs")} testid="settings-doughs" />
      </Card>
      <div className="fixed bottom-0 inset-x-0 z-40 p-4 backdrop-blur-md bg-[#120E0E]/85 border-t border-[#2E2323]">
        <button onClick={save} disabled={saving} data-testid="settings-save-btn"
          className="max-w-3xl mx-auto w-full py-3.5 rounded-full bg-[#DC2626] hover:bg-[#991B1B] text-white font-bold flex items-center justify-center gap-2 disabled:opacity-60">
          {saving ? <Loader2 className="w-5 h-5 animate-spin" /> : <Save className="w-5 h-5" />} Guardar cambios
        </button>
      </div>
    </div>
  );
};
